/**
 * Cache local des images de blocs via Cache Storage API.
 *
 * Stratégie :
 * - Clé de cache = URL de l'image + hash du champ `updated_at` du bloc
 * - Une image est valide tant que `updated_at` n'a pas changé
 * - Si l'image n'est pas en cache ou est périmée → fetch réseau puis mise en cache
 * - Fallback silencieux : en cas d'erreur de cache, on retombe sur l'URL directe
 *
 * Format de clé dans Cache Storage :
 *   `{imageUrl}?_v={encodedUpdatedAt}`
 *
 * Nettoyage :
 * - Limite configurée à MAX_CACHE_BYTES (150 MB par défaut)
 * - Les entrées les plus anciennes (header X-Cached-At) sont supprimées en premier
 */

import { decodeTransportBytes, getApiBase } from '@api/client';

const CACHE_NAME = 'cuxa-block-images-v2';
const MAX_CACHE_BYTES = 150 * 1024 * 1024; // 150 MB
const VERSION_PARAM = '_v';

const isDev = import.meta.env.DEV;

function log(label: string, ...args: unknown[]): void {
  if (isDev) console.debug(`[ImageCache] ${label}`, ...args);
}

/** Encode `updated_at` en token de version court (base64url des 8 premiers chars). */
function toVersionToken(updatedAt: string): string {
  return btoa(updatedAt).replace(/[+/=]/g, (c) => ({ '+': '-', '/': '_', '=': '' }[c] ?? c));
}

/** Construit la clé de cache versionnée à partir de l'URL et du updated_at du bloc. */
function buildCacheKey(imageUrl: string, updatedAt: string): string {
  const sep = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${sep}${VERSION_PARAM}=${toVersionToken(updatedAt)}`;
}

/** Vérifie si Cache Storage est disponible dans ce contexte. */
function isCacheAvailable(): boolean {
  return typeof caches !== 'undefined';
}

/**
 * Récupère une image depuis le cache local ou depuis le réseau.
 * Retourne une blob URL utilisable dans <img src> ou SVG <image href>.
 * En cas d'échec cache, retourne `null` pour que l'appelant utilise l'URL directe.
 */
export async function getCachedBlockImage(
  imagePath: string,
  updatedAt: string
): Promise<string | null> {
  if (!isCacheAvailable()) return null;

  const imageUrl = `${getApiBase()}${imagePath}`;
  const cacheKey = buildCacheKey(imageUrl, updatedAt);

  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);

    if (cached) {
      log('HIT', imagePath);
      const blob = await cached.blob();
      return URL.createObjectURL(blob);
    }

    log('MISS — fetch réseau', imagePath);
    return await fetchAndStore(cache, cacheKey);
  } catch (err) {
    if (isDev) console.warn('[ImageCache] Erreur cache, fallback URL directe', imagePath, err);
    return null;
  }
}

/**
 * Précharge une image en cache sans retourner de blob URL.
 * Idéal pour le prefetch en arrière-plan.
 */
export async function prefetchBlockImage(
  imagePath: string,
  updatedAt: string
): Promise<void> {
  if (!isCacheAvailable()) return;

  const imageUrl = `${getApiBase()}${imagePath}`;
  const cacheKey = buildCacheKey(imageUrl, updatedAt);

  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(cacheKey);
    if (cached) {
      log('PREFETCH — déjà en cache', imagePath);
      return;
    }
    log('PREFETCH — fetch réseau', imagePath);
    await fetchAndStore(cache, cacheKey);
  } catch (err) {
    if (isDev) console.warn('[ImageCache] Erreur prefetch', imagePath, err);
  }
}

/**
 * Invalide toutes les entrées de cache correspondant à un `imagePath` donné,
 * quelle que soit leur version. Utile après mise à jour d'un bloc.
 */
export async function invalidateBlockImageCache(imagePath: string): Promise<void> {
  if (!isCacheAvailable()) return;

  const imageUrl = `${getApiBase()}${imagePath}`;

  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();
    let count = 0;
    for (const req of keys) {
      if (req.url.startsWith(imageUrl)) {
        await cache.delete(req);
        count++;
      }
    }
    log('INVALIDATION', imagePath, `${count} entrée(s) supprimée(s)`);
  } catch (err) {
    if (isDev) console.warn('[ImageCache] Erreur invalidation', imagePath, err);
  }
}

/**
 * Précharge en parallèle (avec limite de concurrence) un tableau de blocs.
 * Conçu pour être appelé à l'ouverture d'une hypothèse.
 */
export async function prefetchBlockImages(
  blocs: Array<{ image_path: string; updated_at: string }>,
  concurrency = 4
): Promise<void> {
  if (!isCacheAvailable() || blocs.length === 0) return;

  log('PREFETCH BATCH', `${blocs.length} images, concurrence=${concurrency}`);

  const queue = [...blocs];

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      const bloc = queue.shift();
      if (!bloc) return;
      await prefetchBlockImage(bloc.image_path, bloc.updated_at);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, blocs.length) }, worker);
  await Promise.all(workers);

  log('PREFETCH BATCH terminé');
}

// ─── Internals ────────────────────────────────────────────────────────────────

const MAX_IMAGE_FETCHES = 4;
let imageFetches = 0;
const imageFetchWaiters: Array<() => void> = [];

async function withImageFetchSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (imageFetches >= MAX_IMAGE_FETCHES) {
    await new Promise<void>((resolve) => imageFetchWaiters.push(resolve));
  }
  imageFetches += 1;
  try {
    return await fn();
  } finally {
    imageFetches -= 1;
    imageFetchWaiters.shift()?.();
  }
}

/** Fetch depuis le réseau, stocke en cache avec métadonnée de date, retourne une blob URL. */
async function fetchAndStore(cache: Cache, cacheKey: string): Promise<string | null> {
  return withImageFetchSlot(() => fetchAndStoreNow(cache, cacheKey));
}

async function fetchAndStoreNow(cache: Cache, cacheKey: string): Promise<string | null> {
  const token = localStorage.getItem('cuxa_token');
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  // Requête sur `cacheKey` (URL inclut `_v` = jeton updated_at), pas sur `imageUrl` seule.
  // Sinon le cache HTTP navigateur peut servir une version périmée du même chemin fichier
  // alors que le disque/serveur ont déjà le nouvel asset (seed / pipeline).
  const response = await fetch(cacheKey, { headers });
  if (!response.ok) {
    log('FETCH ERREUR', cacheKey, response.status);
    return null;
  }

  const bytes = await decodeTransportBytes(new Uint8Array(await response.arrayBuffer()));
  const type = sniffImageType(bytes);
  if (!type) {
    log('FETCH ERREUR', cacheKey, 'image illisible');
    return null;
  }

  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  const blob = new Blob([copy], { type });
  const headers2 = new Headers();
  headers2.set('Content-Type', type);
  headers2.set('X-Cached-At', Date.now().toString());
  await cache.put(cacheKey, new Response(blob.slice(), { status: 200, headers: headers2 }));
  log('STOCKÉ', cacheKey);
  scheduleCacheCleanup();
  return URL.createObjectURL(blob);
}

function sniffImageType(bytes: Uint8Array): string | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

// ─── Nettoyage LRU ────────────────────────────────────────────────────────────

let cleanupScheduled = false;

function scheduleCacheCleanup(): void {
  if (cleanupScheduled) return;
  cleanupScheduled = true;
  // Différer pour ne pas bloquer le flux de chargement
  setTimeout(() => {
    cleanupScheduled = false;
    runCacheCleanup().catch(() => {});
  }, 5000);
}

async function runCacheCleanup(): Promise<void> {
  if (!isCacheAvailable()) return;

  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();

    // Récupérer taille + date pour chaque entrée
    const entries: Array<{ request: Request; size: number; cachedAt: number }> = [];
    let totalBytes = 0;

    for (const req of keys) {
      const res = await cache.match(req);
      if (!res) continue;
      const blob = await res.blob();
      const size = blob.size;
      const cachedAt = parseInt(res.headers.get('X-Cached-At') ?? '0', 10);
      entries.push({ request: req, size, cachedAt });
      totalBytes += size;
    }

    if (totalBytes <= MAX_CACHE_BYTES) {
      log('NETTOYAGE', `${(totalBytes / 1024 / 1024).toFixed(1)} MB — aucun nettoyage nécessaire`);
      return;
    }

    // Trier par date ASC (plus ancien en premier)
    entries.sort((a, b) => a.cachedAt - b.cachedAt);

    let freed = 0;
    for (const entry of entries) {
      if (totalBytes - freed <= MAX_CACHE_BYTES * 0.8) break;
      await cache.delete(entry.request);
      freed += entry.size;
      log('NETTOYAGE suppression', entry.request.url);
    }

    log(
      'NETTOYAGE terminé',
      `libéré ${(freed / 1024 / 1024).toFixed(1)} MB, reste ~${((totalBytes - freed) / 1024 / 1024).toFixed(1)} MB`
    );
  } catch (err) {
    if (isDev) console.warn('[ImageCache] Erreur nettoyage', err);
  }
}
